import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Button from "@components/Button";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { ROUTES } from "@stores/AccountStore";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import AuthLayout, { ErrorAlert } from "@components/AuthLayout";
import { authClient } from "@services/authService";
import Text from "@components/Text";

/**
 * "Forgot password?" — request a reset link by email. Always shows the generic
 * sent state: the endpoint is enumeration-safe and the UI must not reveal
 * whether an account exists.
 */
const PasswordForgotScreen: React.FC = () => {

  const lang = useLang();
  const navigate = useNavigate();
  const { accountStore } = useStores();
  const t = (lang as any).auth ?? {};

  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  if (accountStore.authenticated) {
    return <Navigate to={ROUTES.FIELDS} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await authClient.requestPasswordReset({ email });
      if (error) {
        setError(error.message || "Request failed");
        setLoading(false);
        return;
      }
      setSent(true);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} style={{ width: 336 }}>
        <ErrorAlert enabled={!!error}>{error}</ErrorAlert>
        <Text size={18} weight={500} style={{ textAlign: "center", width: "100%" }}>
          {t.restoreTitle ?? "Restore password"}
        </Text>
        <SizedBox height={8} />
        {sent ? (
          <>
            <Text size={14} color="#899093" style={{ textAlign: "center", width: "100%" }}>
              {t.restoreEmailSent ??
                "If this email exists, we've sent a reset link. Check your inbox."}
            </Text>
            <SizedBox height={8} />
            <Text size={13} color="#899093" style={{ textAlign: "center", width: "100%" }}>
              {t.spamHint ?? "Didn't get it right away? Please check your spam folder."}
            </Text>
          </>
        ) : (
          <>
            <Text size={14} color="#899093" style={{ textAlign: "center", width: "100%" }}>
              {t.restorePasswordEmail ??
                "Enter your account email and we will send you a link to restore password"}
            </Text>
            <SizedBox height={16} />
            <Input
              label={t.emailTitle ?? "Email"}
              placeholder={t.emailField ?? "Enter email"}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <SizedBox height={24} />
            <Button type="submit" disabled={loading}>
              {t.restorePasswordButton ?? "Reset password"}
            </Button>
          </>
        )}
        <SizedBox height={16} />
        <Text
          size={14}
          color="#68AD18"
          style={{ cursor: "pointer", textAlign: "center", width: "100%" }}
          onClick={() => navigate(ROUTES.LOGIN)}
        >
          {t.backToLogin ?? "Back to sign in"}
        </Text>
      </form>
    </AuthLayout>
  );
};

export default observer(PasswordForgotScreen);
